import { Handle, MarkerType, Position } from "@xyflow/react";
import { PlusIcon } from "lucide-react";
import clsx from "clsx";
import { useState } from "react";
import { useFlowStore } from "@/store/flow";

interface NodeLayoutProps {
  id: string;
  children: React.ReactNode;
  color: string;
  hasConnection: boolean;
  isTrigger?: boolean;
}

export const NodeLayout = ({
  id,
  children,
  color,
  hasConnection,
  isTrigger = false,
}: NodeLayoutProps) => {
  const { nodes, setNodes, edges, setEdges, setActiveNode, activeNode } =
    useFlowStore();
  const [isHovered, setIsHovered] = useState(false);

  const addNodeAndConnect = () => {
    // Create the new node
    const position = {
      x: nodes.find((node) => node.id === id)?.position.x || 300,
      y: nodes.find((node) => node.id === id)?.position.y + 100 || 400,
    };
    const newNode = {
      id: (nodes.length + 1).toString(),
      position,
      data: { label: "Select a node type", category: "Empty", type: "empty" },
      type: "empty",
    };

    // Create the edge connecting the current node to the new node
    const newEdge = {
      id: `e${id}-${newNode.id}`,
      source: id,
      target: newNode.id,
      type: "smoothstep", // Use "default" for solid lines
      markerEnd: {
        type: MarkerType.Arrow,
        width: 30,
        height: 30,
      },
    };

    // Update the state
    setNodes([...nodes, newNode]);
    setEdges([...edges, newEdge]);
    setActiveNode(newNode);
  };

  const isActive = activeNode?.id === id;

  return (
    <div
      className={clsx(
        "ring-1 ring-inset rounded-lg",
        `bg-${color}-400/10 ring-${color}-400/60 ring-offset-${color}-500`,
        "p-2 relative",
        "w-[16rem]", // Set a fixed width for all nodes
        activeNode && activeNode.id === id && "ring-offset-2"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex">
        {!isTrigger && (
          <Handle id="target" type="target" position={Position.Top} />
        )}
        {children}
        <Handle id="source" type="source" position={Position.Bottom} />

        {!hasConnection && (
          <div
            className={clsx(
              "absolute top-1/2 left-1/2 transform -translate-x-1/2 bottom-0",
              "transition-all duration-300 ease-in-out",
              isHovered || isActive
                ? "translate-y-[150%] opacity-100"
                : "translate-y-[120%] opacity-0"
            )}
            style={{
              transition: "opacity 0.3s ease, transform 0.3s ease",
            }}
          >
            <div className="border-l h-1.5 mb-0.5 absolute left-1/2 transform -translate-x-1/2 -mt-1.5 dark:border-white/20"></div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                addNodeAndConnect();
              }}
              className="mt-1 bg-blue-500 text-white p-1 rounded-full shadow-lg hover:bg-blue-400 cursor-pointer"
            >
              <PlusIcon className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      {/* <div className="border-t mt-2 pt-1 dark:border-white/20">
        <p className="text-xs">No description</p>
      </div> */}
    </div>
  );
};
